<template>
  <div class="pa-tc pa-access">
    <div class="pa-tc__title">
      <i class="material-icons" aria-hidden="true">person_add</i>
      <span>Add us as an editor on your site</span>
    </div>
    <p class="pa-access__intro">
      Create a dedicated editor-level account for our team (not admin, and not a login anyone else uses).
      On WordPress: Users &gt; Add New &gt; Role: Editor, email <b>devs@pageoptimizer.pro</b>.
    </p>

    <div class="pa-access__choices">
      <label
        v-for="option in options"
        :key="option.key"
        class="pa-access__choice"
        :class="{ 'is-selected': access.method === option.key }"
      >
        <input
          type="radio"
          class="pa-access__input"
          name="pa-publishing-access"
          :value="option.key"
          :checked="access.method === option.key"
          :disabled="readonly"
          @change="update({ method: option.key })"
        />
        <span class="pa-access__rb" aria-hidden="true"></span>
        <span class="pa-access__rl">{{ option.label }}</span>
      </label>
    </div>

    <div v-if="access.method === 'here'" class="pa-access__fields">
      <input
        class="pa-in pa-access__full"
        type="text"
        placeholder="Login URL (e.g. https://yoursite.com/wp-admin)"
        :value="access.login_url"
        :disabled="readonly"
        @input="update({ login_url: $event.target.value })"
      />
      <input
        class="pa-in"
        type="text"
        placeholder="Username"
        autocomplete="off"
        :value="access.username"
        :disabled="readonly"
        @input="update({ username: $event.target.value })"
      />
      <div class="pa-access__password">
        <input
          class="pa-in"
          :type="showPassword ? 'text' : 'password'"
          :placeholder="passwordPlaceholder"
          autocomplete="new-password"
          :value="access.password"
          :disabled="readonly"
          @input="update({ password: $event.target.value })"
        />
        <button
          type="button"
          class="pa-access__eye"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          @click="showPassword = !showPassword"
        >
          <i class="material-icons" aria-hidden="true">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
        </button>
      </div>
      <p class="pa-access__note pa-access__full">
        <i class="material-icons" aria-hidden="true">lock</i>
        <span>Stored encrypted and visible only to the POP Authority publishing team. You can update or revoke access any time from your account.</span>
      </p>
    </div>
    <p v-else-if="access.method === 'granted'" class="pa-access__hint">
      Thanks. We will accept the invite and confirm once we have access.
    </p>
    <p v-else-if="access.method === 'later'" class="pa-access__hint">
      No problem. You can add them later from your account, or use the secure link in your welcome email.
    </p>
  </div>
</template>

<script>
const DEFAULTS = Object.freeze({ method: 'here', login_url: '', username: '', password: '', password_set: false })

/**
 * "Add us as an editor on your site" box of the publishing step (design pa-setup.jsx PublishingStep,
 * update of 2026-10-13): shown when the client chose "Yes, please publish for us". `value` is the
 * form answer `step2.publishing_access` ({ method, login_url, username, password, password_set });
 * the backend strips the password, keeps it encrypted on the site and stores `password_set`
 * instead, so a saved password shows as dots and an empty field on a resubmit keeps it.
 */
export default {
  name: 'PopAuthorityPublishingAccessBox',
  props: {
    value: { type: Object, default: null },
    /** The three choices from the form schema: [{ key, label }]. */
    options: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
  },
  data () {
    return { showPassword: false }
  },
  computed: {
    access () {
      return { ...DEFAULTS, ...(this.value || {}) }
    },
    passwordPlaceholder () {
      return this.access.password_set && !this.access.password ? '••••••••' : 'Password'
    },
  },
  watch: {
    value: {
      // the schema keeps the initial value null: the box writes the default choice into the answers
      handler (value) {
        if (!value || typeof value !== 'object' || !value.method) {
          this.$emit('input', { ...DEFAULTS, ...(value || {}) })
        }
      },
      immediate: true,
    },
  },
  methods: {
    update (patch) {
      if (this.readonly) return
      this.$emit('input', { ...this.access, ...patch })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';

/* design pa.css .pa-tc with the soft border / box background, as the former info box */
.pa-tc {
  border: 1.5px solid $pa-border-soft;
  background: $pa-box-bg;
  border-radius: 10px;
  padding: 14px 16px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1.45;
    margin-bottom: 8px;

    .material-icons {
      color: $pa-info;
      font-size: 19px;
    }
  }
}

.pa-access {
  &__intro {
    margin: 0;
    font-size: 0.88rem;
    line-height: 1.45;
    color: $pa-muted;
  }

  &__choices {
    display: grid;
    gap: 8px;
    margin-top: 12px;
  }

  /* custom radio (CancelSubscriptionModal .pa-cancel__reason) */
  &__choice {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 0.9rem;
    cursor: pointer;
    position: relative;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__rb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid $pa-border;
    flex: none;
    margin-top: 1px;
    position: relative;
    background: #fff;
  }

  &__input:focus-visible + &__rb {
    box-shadow: 0 0 0 3px rgba(255, 87, 0, 0.25);
  }

  &__choice.is-selected &__rb {
    border-color: $pa-accent;

    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      border-radius: 50%;
      background: $pa-accent;
    }
  }

  &__rl {
    font-weight: 600;
    color: $pa-teal;
    line-height: 1.45;
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 12px;
  }

  &__full {
    grid-column: 1 / -1;
  }

  &__password {
    position: relative;

    .pa-in {
      padding-right: 40px;
      box-sizing: border-box;
    }
  }

  &__eye {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: $pa-muted;
    display: inline-flex;

    .material-icons {
      font-size: 20px;
    }
  }

  &__note {
    margin: 0;
    font-size: 0.8rem;
    color: $pa-muted;
    display: flex;
    align-items: center;
    gap: 6px;

    .material-icons {
      font-size: 16px;
    }
  }

  &__hint {
    margin: 10px 0 0;
    font-size: 0.85rem;
    color: $pa-muted;
  }
}

@media (max-width: 640px) {
  .pa-access__fields {
    grid-template-columns: 1fr;
  }
}
</style>
